import * as React from "react";
import executeScriptElements from "../../utils/executeScriptElements";
import SectionHeader from "../molecules/SectionHeader";
import ServerErrorMessage from "../atoms/ServerErrorMessage";
import Loader from "../atoms/Loader";
import useAsyncState from "../../hooks/useAsyncState";

import API, { getUserNonce } from "../../api";

type PropsType = {
    embedded_code: string;
    [key: string]: any;
};

const RTMForm = (props: PropsType) => {
    const [state, localActions] = useAsyncState();
    const scriptRef = React.useRef<HTMLDivElement>(null);

    const prepareFileUploadFields = (
        field: HTMLInputElement | HTMLTextAreaElement
    ) => {
        const fieldLabel = field.parentNode?.querySelector("label");
        
        if (fieldLabel && fieldLabel.textContent?.includes("[file]")) {
            // field is a file upload

            // remove [file] text from input label
            fieldLabel.innerHTML = fieldLabel.textContent.replace("[file]", "");

            // set files being uploaded flag on form container
            scriptRef.current?.setAttribute("data-files-uploading", "true");

            // add parent class - preserve existing classes
            const fieldParent = field.parentNode as Element;
            fieldParent?.classList.add("rtm-form-field--file");
            fieldParent?.classList.add("has-file-upload");

            // hide the real text input
            field.style.display = "none";

            // create and render upload input
            const fileUploadInput = document.createElement("input");
            fileUploadInput.type = "file";
            fileUploadInput.classList.add("rtm-form-fieldInput");
            fileUploadInput.setAttribute("data-file-field", field.name);

            const fieldRequiredAttribute = field.getAttribute("required");
            if (fieldRequiredAttribute) {
                fileUploadInput.setAttribute(
                    "required",
                    fieldRequiredAttribute
                );
            }

            field.parentNode?.appendChild(fileUploadInput);

            field.value = "temp-file-upload";

            // Enhanced file change handler
            fileUploadInput.onchange = (e) => {
                handleFileUploadChange(e);
                
                // Add class to label when file is selected
                const fileInput = e.target as HTMLInputElement;
                if (fileInput.files && fileInput.files.length > 0) {
                    fieldLabel?.classList.add("has-file");
                } else {
                    fieldLabel?.classList.remove("has-file");
                }
            };

            // Add focus/blur handlers to match other fields exactly
            fileUploadInput.addEventListener("focus", () => {
                fieldParent?.classList.add("is-focused");
                // Always animate label up on focus, like the reference form
                fieldLabel?.classList.add("has-file");
            });

            fileUploadInput.addEventListener("blur", () => {
                fieldParent?.classList.remove("is-focused");
                // Keep label up only if a file is selected, otherwise animate back down
                if (!fileUploadInput.files || fileUploadInput.files.length === 0) {
                    fieldLabel?.classList.remove("has-file");
                }
            });
        }
    };

    const handleFileUploadChange = (e: any) => {
        const fileField = e.target;
        const errors: string[] = [];

        // remove old error
        if (fileField.parentNode.querySelector(".react-form__field__errors")) {
            fileField.parentNode
                .querySelector(".react-form__field__errors")
                .remove();
        }
        fileField.removeAttribute("aria-invalid");

        // check for new errors
        if (fileField && fileField.files[0]) {
            // file type validation
            const allowedFiles = [
                ".pdf",
                ".jpeg",
                ".jpg",
                ".png",
                ".apng",
                ".doc",
                ".docx",
                ".xls",
                ".xlsx",
                ".heic",
            ];
            const formatTypes = allowedFiles.join("|");
            const fileTypeRegex = new RegExp(formatTypes, "i");
            if (!fileTypeRegex.exec(fileField.files[0].name)) {
                errors.push("The file format is not allowed. ");
            }

            // file size validation
            if (fileField.files[0].size > 5120 * 1000) {
                errors.push("Please only upload files under 5120kb.");
            }

            if (errors.length) {
                const errorElement = document.createElement("div");
                errorElement.classList.add("react-form__field__errors");
                errorElement.innerText = errors.join(" ");

                fileField.parentNode.appendChild(errorElement);
                fileField.setAttribute("aria-invalid", true);
            }
        }
    };

    const handleFormLoaded = () => {
        // remove any attached stylesheets
        scriptRef.current
            ?.querySelectorAll('style,link[rel="stylesheet"]')
            .forEach(item => item.remove());

        const inputList = scriptRef.current?.querySelectorAll("input");
        const textareaList = scriptRef.current?.querySelectorAll("textarea");

        const rtmFields = [
            ...(inputList ? Array.from(inputList) : []),
            ...(textareaList ? Array.from(textareaList) : []),
        ];

        if (rtmFields) {
            rtmFields.forEach((field, i) => {
                prepareFileUploadFields(field);
                
                // Ensure required fields have proper attributes for CSS asterisk styling
                if (field.hasAttribute('required') || field.getAttribute('aria-required') === 'true') {
                    // Make sure required attribute is set for CSS selector
                    field.setAttribute('required', 'required');
                    
                    // Also set aria-required for accessibility
                    field.setAttribute('aria-required', 'true');
                }
            });
        }
    };

    const handleFormSubmit = (e: any) => {
        const form = e.target;

        // Add was-validated class to the #rtm-form element to enable validation styling
        const rtmFormElement = document.getElementById('rtm-form');
        rtmFormElement?.classList.add('was-validated');

        // Check form validity using browser's built-in validation
        if (!form.checkValidity()) {
            e.preventDefault();
            // Scroll to the first invalid field
            const firstInvalid = form.querySelector(':invalid');
            if (firstInvalid && typeof firstInvalid.scrollIntoView === 'function') {
                firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Optionally, focus the field for accessibility
                if (typeof firstInvalid.focus === 'function') {
                    firstInvalid.focus();
                }
            }
            return false;
        }

        const filesNeedingUpload = scriptRef.current?.getAttribute(
            "data-files-uploading"
        );

        const errorFields = scriptRef.current?.querySelector(
            '[aria-invalid="true"]'
        );

        if (filesNeedingUpload) {
            e.preventDefault();

            if (errorFields) {
                // halt form submission if errors still
                return false;
            }

            localActions.request(
                <>
                    <p>Please wait while we process your submission.</p>
                    <p>Do not refresh the page.</p>
                </>
            );

            // handle file uploads
            handleFileUploads()
                ?.then(() => {
                    localActions.success();

                    scriptRef.current?.removeAttribute("data-files-uploading");

                    (
                        scriptRef.current?.querySelector(
                            `button[type="submit"]`
                        ) as HTMLButtonElement | null
                    )?.click();
                })
                .catch(error => {
                    localActions.fail(
                        error.message || "There was an issue uploading files."
                    );
                });
        }
    };

    const handleFileUploads = () => {
        if (scriptRef.current) {
            const fileUploadFields = scriptRef.current.querySelectorAll(
                'input[type="file"]'
            ) as NodeListOf<HTMLInputElement>;

            if (fileUploadFields && fileUploadFields.length) {
                // collect upload promise for each found file input in form
                return Promise.all(
                    Array.from(fileUploadFields).map(async fileField => {
                        if (
                            fileField.files?.length &&
                            fileField.files?.length > 0
                        ) {
                            const file = fileField.files[0];
                            const fileLabel = (
                                fileField.parentNode as Element
                            ).querySelector("label");
                            const filePrefix = fileLabel
                                ? fileLabel.textContent ?? ""
                                : "";

                            if (file) {
                                const imageUploadForm = new FormData();
                                imageUploadForm.append("file", file);
                                imageUploadForm.append(
                                    "webform-file-upload-nonce",
                                    (await getUserNonce())[
                                        "webform-file-upload-nonce"
                                    ]
                                );
                                imageUploadForm.append(
                                    "file_prefix",
                                    filePrefix
                                );

                                return API.uploadWebformFile(
                                    imageUploadForm
                                ).then(response => {
                                    if (response.annotation_id) {
                                        // set annotation id back in corresponding webform text input
                                        const fileFieldAttribute =
                                            fileField.getAttribute(
                                                "data-file-field"
                                            );
                                        const realInput = fileFieldAttribute
                                            ? document.getElementsByName(
                                                  fileFieldAttribute
                                              )
                                            : [];

                                        if (
                                            realInput[0] &&
                                            realInput[0] instanceof
                                                HTMLInputElement
                                        ) {
                                            realInput[0].value =
                                                response.annotation_id;
                                        }
                                    }
                                });
                            }
                        }
                    })
                );
            }
        }
    };

    React.useEffect(() => {
        if (scriptRef.current) {
            // set embed html
            scriptRef.current.innerHTML = props.embedded_code;

            window.MsCrmMkt = null;

            // execute script tags
            executeScriptElements(scriptRef.current);

            // For RTM forms, we need to wait for the DOM to be ready instead of relying on MsCrmFormLoader
            const checkFormReadyInterval = setInterval(() => {
                const formElements = scriptRef.current?.querySelectorAll("input, textarea");
                
                if (formElements && formElements.length > 0) {
                    clearInterval(checkFormReadyInterval);
                    
                    // Process form fields immediately
                    handleFormLoaded();
                    
                    // Also listen for form submission on the form element directly
                    const formElement = scriptRef.current?.querySelector("form");
                    if (formElement) {
                        formElement.addEventListener("submit", handleFormSubmit);
                        
                        // Also try intercepting button clicks as Microsoft CRM might override form submission
                        const submitButton = formElement.querySelector('button[type="submit"], input[type="submit"]');
                        if (submitButton) {
                            submitButton.addEventListener("click", (e) => {
                                // Create a fake form event to pass to our handler
                                const fakeEvent = {
                                    target: formElement,
                                    preventDefault: () => e.preventDefault()
                                };
                                handleFormSubmit(fakeEvent);
                            });
                        }
                    }
                }
            }, 100);

            // Fallback: if MsCrmFormLoader is available, use it too
            const checkFormLoadedInterval = setInterval(() => {
                if (window.MsCrmMkt && window.MsCrmMkt.MsCrmFormLoader) {
                    clearInterval(checkFormLoadedInterval);

                    // on form load, bind form inputs
                    window.MsCrmMkt.MsCrmFormLoader.on(
                        "afterFormLoad",
                        handleFormLoaded
                    );

                    // form submit event
                    window.MsCrmMkt.MsCrmFormLoader.on(
                        "formSubmit",
                        handleFormSubmit
                    );
                }
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.embedded_code]);

    return (
        <div className="rtm-form" id="rtm-form">
            <SectionHeader
                sectionName="rtm-form"
                title={props.title}
                description={props.description}
            />

            <div className="rtm-form__form" ref={scriptRef}></div>

            <ServerErrorMessage>{state.error}</ServerErrorMessage>

            <Loader
                fixed={true}
                active={state.loading}
                text={state.loadingMessage}
            />
        </div>
    );
};

export default RTMForm;
